import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
  Avatar,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
} from "@mui/material";
import {
  Dashboard,
  Description,
  ChevronLeft,
  ExpandMore,
  ExpandLess,
  Download,
  Logout,
  Person,
  Settings,
  InsertDriveFile,
  Group,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../Logo";
import * as myApi from "../Api";
import ConfirmationDialog from "../UI/ConfirmationDialog";

/**
 * Modern MUI Sidebar Navigation
 * Features:
 * - Always expanded on desktop (280px fixed width)
 * - Drawer on mobile
 * - Logo with link to dashboard
 * - Menu items with icons
 * - Accordion for Papers submenu
 * - Active state highlighting
 * - Smooth animations
 */
export default function LeftSidebar({ user, open, onClose }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Set main page based on user role
  const mainPage = user?.role === "admin" ? "/dashboard/" : "/paper/list/";

  const [papersOpen, setPapersOpen] = React.useState(
    pathname.includes("/paper/")
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const userMenuOpen = Boolean(anchorEl);

  const drawerWidth = 280;

  const handlePapersToggle = () => {
    setPapersOpen(!papersOpen);
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const baseUrl = myApi.baseUrl || "http://localhost:3001";

  const handleDownloadGuidelines = () => {
    window.open(`${baseUrl}/download/author-guidelines`, "_blank");
  };

  const handleDownloadLembarPengesahan = () => {
    window.open(`${baseUrl}/download/lembar-pengesahan`, "_blank");
  };

  const handleDownloadSuratPernyataan = () => {
    window.open(`${baseUrl}/download/surat-pernyataan`, "_blank");
  };

  const handleLogout = () => {
    handleUserMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    // Clear token and user data
    myApi.removeToken();
    myApi.removeUser();
    // Redirect to login
    navigate("/auth/sign-in/");
  };

  const handleProfile = () => {
    handleUserMenuClose();
    navigate("/profile/");
  };

  const getMenuItems = () => {
    const items = [];

    // Dashboard (Admin only)
    if (user?.role === "admin") {
      items.push({
        text: "Dashboard",
        icon: <Dashboard />,
        path: "/dashboard/",
        exact: true,
      });
      items.push({
        text: "User Management",
        icon: <Group />,
        path: "/admin/users/",
        exact: true,
      });
    }

    // Papers section
    items.push({
      text: "Papers",
      icon: <Description />,
      children: [
        {
          text: "Create Paper",
          path: "/paper/add/",
          show: user?.role !== "admin",
        },
        {
          text: "List Paper",
          path: "/paper/list/",
          show: true,
        },
      ],
    });

    return items;
  };

  const menuItems = getMenuItems();

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 64,
        }}
      >
        <Link
          to={mainPage}
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Logo size={60} type="horizontal" />
        </Link>
        {isMobile && (
          <IconButton onClick={onClose}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.children ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handlePapersToggle}
                    sx={{
                      py: 0.75,
                      minHeight: 36,
                      "&:hover": {
                        bgcolor: "primary.lighter",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: papersOpen ? "primary.main" : "inherit",
                      }}
                    >
                      {React.cloneElement(item.icon, {
                        sx: { fontSize: 20 },
                      })}
                    </ListItemIcon>
                    {open && (
                      <>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "0.8125rem",
                            fontWeight: papersOpen ? 600 : 400,
                          }}
                        />
                        {React.cloneElement(papersOpen ? <ExpandLess /> : <ExpandMore />, {
                          sx: { fontSize: 18 },
                        })}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={papersOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 4 }}>
                    {item.children.map((child, childIndex) => {
                      if (child.show === false) return null;
                      const isActive = pathname === child.path;
                      return (
                        <ListItem key={childIndex} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={child.path}
                            onClick={isMobile ? onClose : undefined}
                            sx={{
                              py: 0.625,
                              minHeight: 32,
                              "&:hover": {
                                bgcolor: "primary.lighter",
                              },
                              bgcolor: isActive ? "primary.lighter" : "transparent",
                              borderLeft: isActive
                                ? `3px solid ${theme.palette.primary.main}`
                                : "none",
                            }}
                          >
                            <ListItemText
                              primary={child.text}
                              primaryTypographyProps={{
                                fontSize: "0.75rem",
                                fontWeight: isActive ? 600 : 400,
                                color: isActive ? "primary.main" : "inherit",
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    py: 0.75,
                    minHeight: 36,
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                    bgcolor:
                      pathname === item.path ? "primary.lighter" : "transparent",
                    borderLeft:
                      pathname === item.path
                        ? `3px solid ${theme.palette.primary.main}`
                        : "none",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color:
                        pathname === item.path ? "primary.main" : "inherit",
                    }}
                  >
                    {React.cloneElement(item.icon, {
                      sx: { fontSize: 20 },
                    })}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "0.8125rem",
                        fontWeight: pathname === item.path ? 600 : 400,
                        color:
                          pathname === item.path ? "primary.main" : "inherit",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Bottom Section - Guidelines & Downloads */}
      <Divider />

      {/* Author Guidelines Button */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<InsertDriveFile />}
          onClick={handleDownloadGuidelines}
          sx={{
            justifyContent: "flex-start",
            py: 1,
          }}
        >
          Author Guidelines
        </Button>
      </Box>

      {/* Lembar Pengesahan Button */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<InsertDriveFile />}
          onClick={handleDownloadLembarPengesahan}
          sx={{
            justifyContent: "flex-start",
            py: 1,
          }}
        >
          Lembar Pengesahan
        </Button>
      </Box>

      {/* Surat Pernyataan Button */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<InsertDriveFile />}
          onClick={handleDownloadSuratPernyataan}
          sx={{
            justifyContent: "flex-start",
            py: 1,
          }}
        >
          Surat Pernyataan
        </Button>
      </Box>

      {/* User Menu Button */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleUserMenuClick}
          startIcon={
            <Avatar
              src={myApi.getProfilePictureUrl(user) || undefined}
              sx={{
                width: 24,
                height: 24,
                bgcolor: "grey.400",
                fontSize: "0.75rem",
              }}
            >
              {user?.name?.charAt(0) || "U"}
            </Avatar>
          }
          endIcon={<Settings />}
          sx={{
            justifyContent: "space-between",
            py: 1,
            bgcolor: "grey.400",
            "&:hover": {
              bgcolor: "primary.dark",
            },
          }}
        >
          <Typography variant="body2" fontWeight={500} noWrap sx={{ mr: 1 }}>
            Account
          </Typography>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={userMenuOpen}
          onClose={handleUserMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 2,
            },
          }}
          transformOrigin={{ horizontal: "left", vertical: "bottom" }}
          anchorOrigin={{ horizontal: "left", vertical: "top" }}
        >
          {/* User Info Header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={myApi.getProfilePictureUrl(user) || undefined}
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "grey.400",
                  fontSize: "1.25rem",
                }}
              >
                {user?.name?.charAt(0) || "U"}
              </Avatar>
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                  {user?.name || "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user?.role === "admin" ? "Administrator" : "Participant"}
                </Typography>
              </Box>
            </Box>
            {user?.email && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
                noWrap
              >
                {user.email}
              </Typography>
            )}
          </Box>

          <Divider />

          <MenuItem onClick={handleProfile}>
            <MuiListItemIcon>
              <Person fontSize="small" />
            </MuiListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
            <MuiListItemIcon>
              <Logout fontSize="small" />
            </MuiListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          © 2026 KARSA NUSA
        </Typography>
      </Box>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmation
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: Always expanded, fixed width
  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

// Logout Confirmation Dialog
function LogoutConfirmation({ open, onClose, onConfirm }) {
  return (
    <ConfirmationDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Logout Confirmation"
      message="Are you sure you want to logout? You will need to sign in again to access your account."
      confirmText="Logout"
      cancelText="Cancel"
      variant="warning"
    />
  );
}
